/**
 * NAUD brand icon layer (NAU-247).
 *
 * Ported to match the product team's reference implementation ("Dashboard View
 * Codex"): bold OUTLINE Lucide glyphs in the NAUD brand palette, inside circles
 * — a white circle + coloured ring halo for the KPI tiles, a tinted circle for
 * the smaller badges. (Not solid-filled — the earlier filled version was wrong.)
 */

import type { LucideIcon } from "lucide-react";

import type { DashboardStatus } from "@/lib/dashboard/traffic-light";

interface ToneStyle {
  /** Glyph / accent colour */
  solid: string;
  /** Tinted-circle background (small badges) */
  tint: string;
  /** Ring-halo colour (KPI tile badges) */
  ring: string;
}

export const NAUD = {
  navy: { solid: "#061968", tint: "#eef0f8", ring: "#dfe3f3" },
  blue: { solid: "#2457ff", tint: "#eef2ff", ring: "#dfe5ff" },
  red: { solid: "#ff1e1e", tint: "#fff1f2", ring: "#ffd9dc" },
  orange: { solid: "#ff6b1a", tint: "#fff4ec", ring: "#ffd9c2" },
  green: { solid: "#10a64a", tint: "#eafaf0", ring: "#cdeed9" },
  purple: { solid: "#c026d3", tint: "#fdf4ff", ring: "#f5d0fe" },
} satisfies Record<string, ToneStyle>;

export type NaudTone = keyof typeof NAUD;

export function toneFromStatus(status: DashboardStatus): NaudTone {
  if (status === "ACTION") return "red";
  if (status === "NEEDS_ATTENTION") return "orange";
  return "green";
}

const TINT_SIZES = {
  sm: { box: 32, icon: 19 },
  md: { box: 48, icon: 25 },
  lg: { box: 40, icon: 20 },
} as const;

interface NaudBadgeIconProps {
  icon: LucideIcon;
  tone: NaudTone;
  size?: keyof typeof TINT_SIZES;
  strokeWidth?: number;
}

/** Small badge: a coloured glyph in a tinted circle (today's status, insights,
 *  priority rows). */
export function NaudBadgeIcon({
  icon: Icon,
  tone,
  size = "md",
  strokeWidth = 2.6,
}: NaudBadgeIconProps) {
  const s = TINT_SIZES[size];
  const c = NAUD[tone];
  return (
    <div
      className="grid flex-none place-items-center rounded-full"
      style={{ width: s.box, height: s.box, backgroundColor: c.tint }}
    >
      <Icon size={s.icon} strokeWidth={strokeWidth} color={c.solid} />
    </div>
  );
}

interface NaudRingBadgeProps {
  icon: LucideIcon;
  tone: NaudTone;
}

/** KPI-tile badge: a bold outline glyph in a white circle with a coloured ring
 *  halo — matches the reference MetricCard treatment. */
export function NaudRingBadge({ icon: Icon, tone }: NaudRingBadgeProps) {
  const c = NAUD[tone];
  return (
    <div
      className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white"
      style={{ boxShadow: `0 0 0 8px ${c.ring}` }}
    >
      <Icon size={30} strokeWidth={3.2} color={c.solid} />
    </div>
  );
}
